/**
 * The people in one tenant, as a Platform Admin sees them.
 *
 * @module components/platform/TenantUserList
 */

import React from "react";

import { roleLabel } from "@/components/shared/role-labels";
import {
  Badge,
  Button,
  CollectionView,
  type Column,
  countLabel,
  EmptyState,
  ErrorAlert,
  MutedText,
  Panel,
  StatusBadge,
} from "@/components/ui";
import type { UserSummary } from "@/platform";

export interface TenantUserListProps {
  users: readonly UserSummary[];
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  /**
   * Dev builds only; absent everywhere else, and the column goes with it.
   * Not named for impersonation: a prop name survives minification, and
   * the production build refuses a bundle that carries that word.
   */
  UserAction?: React.ComponentType<{ user: UserSummary }>;
}

/**
 * @example
 * ```tsx
 * <TenantUserList users={users} loading={loading} error={error} />
 * ```
 */
export function TenantUserList({
  users,
  loading,
  error,
  onRetry = undefined,
  UserAction = undefined,
}: TenantUserListProps): React.ReactElement {
  const columns: Column<UserSummary>[] = [
    {
      key: "user",
      header: "User",
      render: (user) => (
        <div>
          <div className="font-semibold">{user.displayName}</div>
          <MutedText as="span" size="caption">
            {user.email}
          </MutedText>
        </div>
      ),
      sortValue: (user) => user.displayName,
    },
    { key: "role", header: "Role", render: (user) => roleLabel(user.accountRole) },
    { key: "status", header: "Status", render: (user) => <StatusBadge status={user.status} /> },
  ];
  if (UserAction) {
    columns.push({
      key: "actions",
      header: "Actions",
      hideHeader: true,
      align: "end",
      render: (user) => <UserAction user={user} />,
    });
  }

  return (
    <Panel
      headingLevel={2}
      title="Users"
      actions={
        // A failed read has no count to give.
        loading || error ? null : (
          <Badge
            tone="ink"
            variant="soft"
            size="sm"
            aria-label={`${countLabel(users.length, "user")} in this tenant`}
          >
            {users.length}
          </Badge>
        )
      }
    >
      <CollectionView
        storageKey="platform-console-users"
        caption="Tenant users"
        columns={columns}
        rows={users}
        rowKey={(user) => user.id}
        loading={loading}
        error={
          error && !loading ? (
            <ErrorAlert data-testid="platform-console-users-error">
              <span className="flex flex-wrap items-center gap-3">
                {error}
                {onRetry ? (
                  <Button size="sm" variant="outline" icon="arrow-clockwise" onClick={onRetry}>
                    Try again
                  </Button>
                ) : null}
              </span>
            </ErrorAlert>
          ) : undefined
        }
        verticalAlign="top"
        emptyState={
          <EmptyState icon="users-three" title="No users found for this tenant" density="row" />
        }
        data-testid="platform-console-users"
      />
    </Panel>
  );
}
